"use client";

import { CloseSmall } from "@ellaandil/ui";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { type PropsWithChildren, type ReactNode, useState } from "react";
import { Typography } from "@/components/ui/Typography";

const TooltipArrow = () => (
	<TooltipPrimitives.Arrow asChild>
		<svg
			width="16"
			height="8"
			viewBox="0 0 16 8"
			className="absolute rotate-180 border-b border-solid border-status-information-bg -translate-y-0.5"
			aria-hidden="true"
		>
			<path
				d="M0 8L8 0L16 8"
				className="fill-status-information-bg stroke-status-information-surface"
				strokeWidth="1"
			/>
		</svg>
	</TooltipPrimitives.Arrow>
);

export interface TooltipProps extends PropsWithChildren {
	tip?: {
		header?: string;
		content?: ReactNode;
	};
	trigger?: "hover" | "click";
}

export const Tooltip = ({ children, tip, trigger = "hover" }: TooltipProps) => {
	const [open, setOpen] = useState(false);

	if (trigger === "click") {
		return (
			<TooltipPrimitives.Provider delayDuration={0}>
				<TooltipPrimitives.Root open={open} onOpenChange={setOpen}>
					<TooltipPrimitives.Trigger asChild onClick={() => setOpen(!open)}>
						{children}
					</TooltipPrimitives.Trigger>
					<TooltipPrimitives.Portal>
						<TooltipPrimitives.Content
							className="z-50 rounded-md bg-status-information-bg border border-solid border-status-information-surface p-3 text-xs text-neutral-white-100 shadow-lg w-[calc(100vw-2rem)] md:w-auto"
							side="bottom"
							sideOffset={10}
							align="end"
							alignOffset={0}
							onPointerDownOutside={() => setOpen(false)}
						>
							{tip && (
								<div className="flex flex-col gap-2 md:min-w-[320px]">
									<div className="flex items-center w-full justify-between">
										<Typography
											variant="body-sm-bold"
											className="text-status-information-text"
											style={{ fontWeight: 700 }}
										>
											{tip.header}
										</Typography>

										<CloseSmall
											onClick={() => setOpen(false)}
											className="size-4 text-shape-dark-fill-1"
										/>
									</div>
									{tip.content}
								</div>
							)}
							<TooltipArrow />
						</TooltipPrimitives.Content>
					</TooltipPrimitives.Portal>
				</TooltipPrimitives.Root>
			</TooltipPrimitives.Provider>
		);
	}

	return (
		<TooltipPrimitives.Provider delayDuration={0}>
			<TooltipPrimitives.Root>
				<TooltipPrimitives.Trigger asChild>{children}</TooltipPrimitives.Trigger>
				<TooltipPrimitives.Portal>
					<TooltipPrimitives.Content
						className="z-50 rounded-md bg-status-information-bg border border-solid border-status-information-surface p-3 text-xs text-neutral-white-100 shadow-lg w-[calc(100vw-2rem)] md:w-auto"
						side="bottom"
						sideOffset={5}
						align="end"
						alignOffset={0}
					>
						{tip && (
							<div className="flex flex-col gap-2 md:min-w-[320px]">
								<div className="flex items-center w-full justify-between">
									<Typography
										variant="body-sm-bold"
										className="text-status-information-text"
										style={{ fontWeight: 700 }}
									>
										{tip.header}
									</Typography>

									<CloseSmall
										onClick={() => setOpen(false)}
										className="size-4 text-shape-dark-fill-1"
									/>
								</div>
								{tip.content}
							</div>
						)}
						<TooltipArrow />
					</TooltipPrimitives.Content>
				</TooltipPrimitives.Portal>
			</TooltipPrimitives.Root>
		</TooltipPrimitives.Provider>
	);
};
